import {
  PROJECT_COST_BANDS,
  PROJECT_COST_PROFILE_LABEL,
  PROJECT_COST_PROFILE_ORDER,
  PROJECT_COST_SCOPE_LABELS,
  formatProjectCostDuration,
  formatProjectCostRange,
  resolveProjectCostProfile,
} from '@/lib/projectCostBands.shared.js';

export type ProjectCostLang = 'tr' | 'en';

export type ProjectCostProfile =
  | 'marketing_site'
  | 'saas_mvp'
  | 'mobile_product'
  | 'ecommerce'
  | 'general';

/** explicit = user picked the type or text named exactly one; mixed/weak/unknown get a visible notice. */
export type ProjectCostConfidence = 'selected' | 'explicit' | 'mixed' | 'weak' | 'unknown';

export type ProjectCostEstimate = {
  profile: ProjectCostProfile;
  profileLabel: string;
  confidence: ProjectCostConfidence;
  matchedProfiles: ProjectCostProfile[];
  /** Shown above the bands when the type was not stated unambiguously; null otherwise. */
  profileNotice: string | null;
  lines: string[];
  labels: string[];
  methodologyNote: string;
};

type BandKey = 'mvp' | 'platform' | 'design' | 'maintenance';
const BAND_KEYS: BandKey[] = ['mvp', 'platform', 'design', 'maintenance'];

export const PROJECT_COST_PROFILES = PROJECT_COST_PROFILE_ORDER as ProjectCostProfile[];

export function isProjectCostProfile(value: unknown): value is ProjectCostProfile {
  return typeof value === 'string' && (PROJECT_COST_PROFILES as string[]).includes(value);
}

export function classifyProjectCostProfile(projectInput: string): ProjectCostProfile {
  return resolveProjectCostProfile(projectInput).profile as ProjectCostProfile;
}

export function resolveProjectCostSelection(
  projectInput: string,
  selectedProfile?: ProjectCostProfile | null,
): { profile: ProjectCostProfile; confidence: ProjectCostConfidence; matched: ProjectCostProfile[] } {
  const inferred = resolveProjectCostProfile(projectInput);
  if (selectedProfile && isProjectCostProfile(selectedProfile)) {
    return { profile: selectedProfile, confidence: 'selected', matched: inferred.matched as ProjectCostProfile[] };
  }
  return {
    profile: inferred.profile as ProjectCostProfile,
    confidence: inferred.confidence,
    matched: inferred.matched as ProjectCostProfile[],
  };
}

/**
 * Contact form project type for a cost estimate, derived from the same profile as the bands so the
 * quote form cannot say "mobile" for an e-commerce estimate. AI wording keeps the AI type.
 */
export function contactProjectTypeForCostProfile(
  profile: ProjectCostProfile,
  projectInput: string,
): 'ai' | 'saas' | 'mobile' | 'custom' | 'other' {
  if (profile === 'saas_mvp') return 'saas';
  if (profile === 'mobile_product') return 'mobile';
  if (profile === 'marketing_site') return 'other';
  if (/(^|\s)(ai|yapay zeka|yapay zek[aâ]|llm|chatgpt|gpt)(\s|$)/i.test(projectInput.trim())) return 'ai';
  return 'custom';
}

export function getProjectCostProfileLabel(profile: ProjectCostProfile, lang: ProjectCostLang): string {
  return PROJECT_COST_PROFILE_LABEL[profile][lang];
}

function buildProfileNotice(
  confidence: ProjectCostConfidence,
  profile: ProjectCostProfile,
  matched: ProjectCostProfile[],
  lang: ProjectCostLang,
): string | null {
  const label = getProjectCostProfileLabel(profile, lang);
  if (confidence === 'mixed') {
    const others = matched
      .filter((p) => p !== profile)
      .map((p) => getProjectCostProfileLabel(p, lang))
      .join(', ');
    return lang === 'tr'
      ? `Tarifiniz birden fazla proje türü içeriyor (${label} + ${others}). Bantlar "${label}" profiline göre; diğer bileşen ayrı kapsamlanır. Doğru türü "Proje profili" alanından seçebilirsiniz.`
      : `Your description covers more than one project type (${label} + ${others}). Bands use "${label}"; the other part is scoped separately. Pick the right type in the "Project profile" field.`;
  }
  if (confidence === 'weak') {
    return lang === 'tr'
      ? `Proje türü tarifinizden tahmin edildi: "${label}". Farklıysa "Proje profili" alanından seçin.`
      : `Project type inferred from your description: "${label}". If that is wrong, pick it in the "Project profile" field.`;
  }
  if (confidence === 'unknown') {
    return lang === 'tr'
      ? `Proje türü tarifinizden anlaşılamadı; genel yazılım bandı gösteriliyor. Daha doğru aralık için "Proje profili" alanından türü seçin (ör. kurumsal site, SaaS, mobil, e-ticaret).`
      : `We could not tell the project type from your description, so the general software band is shown. Pick the type in the "Project profile" field (e.g. corporate site, SaaS, mobile, e-commerce) for a closer range.`;
  }
  return null;
}

export type ProjectCostBandRow = {
  scopeLabel: string;
  range: string;
  duration: string;
};

/** Editorial / blog tables — same numbers as the cost estimator tool. */
export function getProjectCostBandRows(
  profile: ProjectCostProfile,
  lang: ProjectCostLang,
): ProjectCostBandRow[] {
  const bands = PROJECT_COST_BANDS[profile];
  return BAND_KEYS.map((key, index) => {
    const band = bands[key];
    return {
      scopeLabel: PROJECT_COST_SCOPE_LABELS[lang][index],
      range: formatProjectCostRange(band.min, band.max, lang),
      duration: formatProjectCostDuration(band.weeksMin, band.weeksMax, lang),
    };
  });
}

export function estimateProjectCost(
  projectInput: string,
  lang: ProjectCostLang,
  selectedProfile?: ProjectCostProfile | null,
): ProjectCostEstimate {
  const { profile, confidence, matched } = resolveProjectCostSelection(projectInput, selectedProfile);
  const name = (projectInput || (lang === 'tr' ? 'projeniz' : 'your project')).trim();
  const profileLabel = getProjectCostProfileLabel(profile, lang);
  const labels = PROJECT_COST_SCOPE_LABELS[lang];

  const lines = getProjectCostBandRows(profile, lang).map((row) =>
    lang === 'tr'
      ? `"${name}" — ${row.scopeLabel}: ${row.range} (${row.duration}) · profil: ${profileLabel}`
      : `"${name}" — ${row.scopeLabel}: ${row.range} (${row.duration}) · profile: ${profileLabel}`,
  );

  const mobileTimelineNote =
    profile === 'mobile_product'
      ? lang === 'tr'
        ? ' Mobil MVP süresi projeye göre 1–2 haftadan 6–8 haftaya kadar değişir.'
        : ' Depending on the project, a mobile MVP takes from 1–2 weeks to 6–8 weeks.'
      : '';

  const methodologyNote =
    (lang === 'tr'
      ? 'Örnek bütçe tablosu: TRY, KDV hariç; premium butik ajans bandları (üst segment TR + global SaaS referansı, Ekim 2026 kalibrasyonu). Entegrasyon, veri taşıma, uyumluluk, QA ve DevOps kapsam dışıdır. Süreler 8 haftaya kadar hafta, üstünde tam ay (4 hafta = 1 ay) olarak yuvarlanır. Bantlar yalnızca ön fikir verir; fiyat proje bazında belirlenir: projeyi analiz edip görev listesini ve eforu çıkarırız, projeye uygun outsource ekip kurulur ve fiyat bu efor/maliyetten hesaplanarak yazılı teklifte yer alır.'
      : 'Illustrative TRY ranges excl. VAT; premium boutique bands (upper-tier TR + global SaaS benchmarks, Oct 2026 calibration). Excludes integrations, migration, compliance, QA, and DevOps unless scoped. Timelines up to 8 weeks are shown in weeks, longer ones in whole months (4 weeks = 1 month). Bands are indicative only — pricing is set per project: we analyse the project, derive the task list and effort, assemble an outsourced team for it, and calculate the price from that effort and cost in a written proposal.') + mobileTimelineNote;

  return {
    profile,
    profileLabel,
    confidence,
    matchedProfiles: matched,
    profileNotice: buildProfileNotice(confidence, profile, matched, lang),
    lines,
    labels: [...labels],
    methodologyNote,
  };
}
